import React, { useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { Child } from "../types/auth";
import Logo from "./Logo";
import emailjs from "emailjs-com";
import Footer from "./Footer";
import WhatsAppButton from "./WhatsappButton";

const RegisterForm: React.FC = () => {
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    phone: "",
  });
  const [children, setChildren] = useState<
    Omit<Child, "id" | "parentId" | "registrationDate">[]
  >([{ name: "", dateOfBirth: "", group: "" }]);
  const [error, setError] = useState("");
  const [emailSuccess, setEmailSuccess] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const { t } = useLanguage();

  const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || "";
  const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_REGISTER_TEMPLATE_ID || "";
  const USER_ID = process.env.REACT_APP_EMAILJS_USER_ID || "";

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleChildChange = (index: number, field: string, value: string) => {
    const updatedChildren = [...children];
    updatedChildren[index] = { ...updatedChildren[index], [field]: value };
    setChildren(updatedChildren);
  };

  const addChild = () => {
    setChildren([...children, { name: "", dateOfBirth: "", group: "" }]);
  };

  const removeChild = (index: number) => {
    if (children.length > 1) {
      setChildren(children.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setEmailSuccess(null);
    setEmailError(null);

    try {
      // Prepare children info as a string
      const childrenInfo = children
        .map(
          (child, idx) =>
            `Child ${idx + 1}:\n\t\t  Name: ${child.name}\n\t\t  Birth Date: ${
              child.dateOfBirth
            }\n\t\t  Group: ${child.group}`
        )
        .join("\n\n");

          const content = `
            New Parent Registration
            -----------------------
            Parent Name: ${formData.name}
            Email: ${formData.email}
            Phone: ${formData.phone}

            Children Registered:
                ${childrenInfo}

            Form Type: Register `.trim();
      // Send email via EmailJS
      await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          form_type: "Register",
          content: content,
        },
        USER_ID
      );
      setEmailSuccess(
        t("form.sendSuccess") || "Registration email sent successfully!"
      );

      // ✅ Reset the form
      setFormData({
        email: "",
        name: "",
        phone: "",
      });
      setChildren([{ name: "", dateOfBirth: "", group: "" }]); // reset to one empty child

      setTimeout(() => {
        setEmailSuccess(null);
      }, 3000);
    } catch (err) {
      setError(t("auth.registrationFailed"));
      setEmailError(
        t("form.sendError") || "Failed to send registration email."
      );
    }
  };

  return (
    <div>
      <div className="min-h-screen bg-gradient-section py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-dots-pattern bg-dots opacity-5"></div>
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="text-center mb-10">
            <div className="flex justify-center mb-6">
              <div className="floating">
                <Logo size="xlarge" />
              </div>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-child font-bold text-neutral-800 mb-4">
              {t("auth.registerTitle")}
            </h2>
            <p className="text-xl text-neutral-800 font-medium">
              {t("auth.registerSubtitle")}
            </p>
          </div>

          <div className="card bg-[#FBEACD]">
            <form className="space-y-8" onSubmit={handleSubmit}>
              {/* Parent Information */}
              <div className="space-y-6">
                <h3 className="text-2xl font-child font-bold text-neutral-800 mb-6">
                  {t("auth.parentInfo")}
                </h3>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-lg font-child font-semibold text-neutral-800 mb-3"
                    >
                      {t("auth.fullName")}
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="input-field"
                      placeholder={t("auth.fullName")}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-lg font-child font-semibold text-neutral-800 mb-3"
                    >
                      {t("auth.phone")}
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      pattern="[0-9]*"
                      inputMode="numeric"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="input-field"
                      placeholder={t("auth.phone")}
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-lg font-child font-semibold text-neutral-800 mb-3"
                  >
                    {t("auth.email")}
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder={t("auth.email")}
                  />
                </div>
              </div>

              {/* Children Information */}
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-2xl font-child font-bold text-neutral-800">
                    {t("auth.children")}
                  </h3>
                  <button
                    type="button"
                    onClick={addChild}
                    className="0 text-[#492A19] bg-[#FBEACD] border-2 border-[#492A19] px-6 py-3 rounded-full font-child font-bold hover:bg-[#492A19] hover:text-[#F9DCAC] transition-all duration-300 shadow-lg hover:shadow-xl"
                  >
                    {t("auth.addChild")}
                  </button>
                </div>

                {children.map((child, index) => (
                  <div
                    key={index}
                    className="border border-[#492A19] rounded-2xl p-6 space-y-6 bg-gradient-card"
                  >
                    <div className="flex justify-between items-center">
                      <h4 className="font-child font-bold text-xl text-neutral-800">
                        {t("auth.childNumber")} {index + 1}
                      </h4>
                      {children.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeChild(index)}
                          className="text-red-500 hover:text-red-700 font-medium transition-colors"
                        >
                          {t("auth.removeChild")}
                        </button>
                      )}
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-lg font-child font-semibold text-neutral-800 mb-3">
                          {t("auth.childname")}
                        </label>
                        <input
                          type="text"
                          required
                          value={child.name}
                          onChange={(e) =>
                            handleChildChange(index, "name", e.target.value)
                          }
                          className="input-field"
                          placeholder={t("auth.childname")}
                        />
                      </div>

                      <div>
                        <label className="block text-lg font-child font-semibold text-neutral-800 mb-3">
                          {t("auth.childBirthDate")}
                        </label>
                        <input
                          type="date"
                          required
                          value={child.dateOfBirth}
                          onChange={(e) =>
                            handleChildChange(
                              index,
                              "dateOfBirth",
                              e.target.value
                            )
                          }
                          className="input-field"
                        />
                      </div>

                      <div>
                        <label className="block text-lg font-child font-semibold text-neutral-800 mb-3">
                          {t("auth.childGroup")}
                        </label>
                        <select
                          value={child.group}
                          onChange={(e) =>
                            handleChildChange(index, "group", e.target.value)
                          }
                          className="input-field appearance-none"
                          style={{
                            backgroundImage: `url("data:image/svg+xml;utf8,<svg fill='none' stroke='%23492A19' stroke-width='2' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'><path d='M19 9l-7 7-7-7'/></svg>")`,
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "right 1rem center", // 👈 aligns to the right side
                            backgroundSize: "1.25rem",
                          }}
                        >
                          <option value="">{t("auth.selectGroup")}</option>
                          <option value="Baby's">{t("auth.babies")}</option>
                          <option value="Dreumesen">
                            {t("auth.toddlers")}
                          </option>
                          <option value="Peuters">
                            {t("auth.preschoolers")}
                          </option>
                          <option value="Kleuters">
                            {t("auth.kindergarten")}
                          </option>
                          <option value="Schoolkinderen">
                            {t("auth.schoolAge")}
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {error && (
                <div className="bg-red-50 border-2 border-red-200 text-red-700 px-6 py-4 rounded-2xl font-medium">
                  {error}
                </div>
              )}
              {emailSuccess && (
                <div className="bg-green-50 border-2 border-green-200 text-green-700 px-6 py-4 rounded-2xl font-medium mt-4">
                  {emailSuccess}
                </div>
              )}
              {emailError && (
                <div className="bg-red-50 border-2 border-red-200 text-red-700 px-6 py-4 rounded-2xl font-medium mt-4">
                  {emailError}
                </div>
              )}

              <div className="space-y-6">
                <button
                  type="submit"
                  className="btn-primary bg-[#FBEACD] border border-[#492A19] w-full disabled:opacity-50 disabled:cursor-not-allowed text-xl text-[#492A19] py-5"
                >
                  {t("auth.register")}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* WhatsApp Floating Contact Button */}
      <WhatsAppButton />
      <Footer />
    </div>
  );
};

export default RegisterForm;
